import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { merge, Subscription } from 'rxjs';
import { SUPER_ADMIN_LEVEL } from '../../constants/access-levels';
import { ConfigListEntry } from '../../shared/app-config.model';
import { ConfigService } from '../../services/config.service';
import { environment } from '../../../environments/environment';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { expandConfigKeysByCompanyKey, parseAllowedClientConfigKeysFromUser } from '../../utils/config-list-expand';

@Component({
	selector: 'app-config-switcher',
	templateUrl: './config-switcher.component.html',
	styleUrls: ['./config-switcher.component.scss']
})
export class ConfigSwitcherComponent implements OnInit, OnDestroy {
	availableConfigs: ConfigListEntry[] = [];
	currentConfig = '';
	selectedConfig: any = ''
	user: any = [];
	@Output() reload = new EventEmitter < boolean > ();
	private subscriptions: Subscription = new Subscription();
	private fullConfigList: ConfigListEntry[] = [];

	constructor(
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private configService: ConfigService
	) {
		this.globalSearchService.user.subscribe(r => {
			this.user = r;
		});
	}

	ngOnInit(): void {
		this.subscriptions.add(
			this.configService.loadAvailableConfigs().subscribe(
				configs => {
					this.fullConfigList = configs || [];
					this.applyAvailableConfigsFilter();
				},
				error => console.error('Error loading available configs', error)
			)
		);

		this.subscriptions.add(
			merge(
				this.globalSearchService.user,
				this.globalSearchService.configsubscription
			).subscribe(() => this.applyAvailableConfigsFilter())
		);
	}

	private applyAvailableConfigsFilter(): void {
		if (!this.fullConfigList.length) {
			this.availableConfigs = [];
			return;
		}

		const cfg = this.globalSearchService.config;
		const u = this.globalSearchService.user.getValue();

		const serviceKey = this.configService.getCurrentConfig();
		const envClient = cfg?.env?.client ?? environment.name;
		this.currentConfig = this.resolveActiveConfigKey(cfg, envClient, serviceKey);

		const level = u?.user?.fullaccess != null ? parseInt(String(u.user.fullaccess), 10) : NaN;
		if (Number.isNaN(level)) {
			this.availableConfigs = [];
			return;
		}

		if (level >= SUPER_ADMIN_LEVEL) {
			this.availableConfigs = this.fullConfigList.filter((config) =>
				level >= (config.allowedAccessLevel ?? SUPER_ADMIN_LEVEL)
			);
			this.syncSelectionToLoadedTenant(envClient);
			return;
		}

		// Scope must follow the *loaded* config (env.client), not the stored file key — a stale v8ActiveSetupKey
		// would point at another company's row and filter the list to zero (dropdown disappears).
		let active = this.fullConfigList.find((e) => e.config === envClient);
		if (!active && serviceKey) {
			active = this.fullConfigList.find((e) => e.config === serviceKey);
		}
		const scope = active?.companyKey;
		if (!scope) {
			this.availableConfigs = [];
			return;
		}

		const allowedKeys = parseAllowedClientConfigKeysFromUser(u?.user);
		const hasAllowlist = allowedKeys.length > 0;
		const expandedKeys = hasAllowlist ? expandConfigKeysByCompanyKey(allowedKeys, this.fullConfigList) : [];
		const allow = hasAllowlist ? new Set(expandedKeys) : null;

		if (hasAllowlist) {
			this.availableConfigs = this.fullConfigList.filter(
				(c) => c.companyKey === scope && allow!.has(c.config)
			);
			this.syncSelectionToLoadedTenant(envClient);
			return;
		}

		this.availableConfigs = this.fullConfigList.filter(
			(config) =>
				config.companyKey === scope &&
				level >= (config.allowedAccessLevel ?? SUPER_ADMIN_LEVEL)
		);
		this.syncSelectionToLoadedTenant(envClient);
	}

	/**
	 * Resolve which config *file* key matches the loaded runtime config.
	 * Loaded env.client wins over a stale localStorage key from another company (moov vs hairlines).
	 * km / kmproca share companyKey and keep the stored file key when scope still matches.
	 */
	private resolveActiveConfigKey(cfg: any, envClient: string, serviceKey: string): string {
		const list = this.fullConfigList;
		const keyEntry = serviceKey ? list.find((e) => e.config === serviceKey) : undefined;
		const envEntry = envClient ? list.find((e) => e.config === envClient) : undefined;
		const loadedClient = cfg?.env?.client;

		if (keyEntry && loadedClient && keyEntry.companyKey !== loadedClient && keyEntry.config !== loadedClient) {
			if (envEntry) {
				return envEntry.config;
			}
			const scoped = list.find((e) => e.companyKey === loadedClient);
			if (scoped) {
				return scoped.config;
			}
		}

		if (keyEntry && loadedClient && keyEntry.companyKey === loadedClient) {
			return serviceKey;
		}

		if (envEntry && loadedClient) {
			return envEntry.config;
		}
		if (keyEntry) {
			return serviceKey;
		}
		return envClient;
	}

	/** Keep the dropdown aligned with the loaded tenant — never auto-switch to another company on refresh. */
	private syncSelectionToLoadedTenant(envClient: string): void {
		if (!this.availableConfigs.length) {
			return;
		}

		const cfg = this.globalSearchService.config;
		const tenantScope = cfg?.env?.client ?? envClient;
		const resolved = this.resolveActiveConfigKey(
			cfg,
			envClient,
			this.configService.getCurrentConfig()
		);

		const tenantPool = tenantScope
			? this.availableConfigs.filter(
				(c) => c.companyKey === tenantScope || c.config === tenantScope
			)
			: [];
		const pool = tenantPool.length ? tenantPool : this.availableConfigs;

		let next = resolved;
		if (!pool.some((c) => c.config === next)) {
			next = pool.find((c) => c.config === tenantScope)?.config
				?? pool.find((c) => c.companyKey === tenantScope)?.config
				?? pool[0].config;
		}

		this.currentConfig = next;
		if (next !== this.configService.getCurrentConfig()
			|| this.configService.isSetupKeyStaleForLoadedConfig(cfg, this.fullConfigList)) {
			this.configService.setActiveSetupKey(next);
		}
	}

	onChangeConfig(newConfig: string): void {
		if (!newConfig) {
			return;
		}

		const cfg = this.globalSearchService.config;
		const envClient = cfg?.env?.client ?? environment.name;
		const loadedKey = this.resolveActiveConfigKey(cfg, envClient, this.configService.getCurrentConfig());

		// Ignore spurious select change events while syncing UI to the already-loaded tenant.
		if (newConfig === loadedKey) {
			this.currentConfig = loadedKey;
			return;
		}

		this.currentConfig = newConfig;

		const loadConfigSubscription = this.configService.loadConfig(newConfig).subscribe(
			r => {
				this.globalSearchService.setConfig(r);
				this.globalsService.updateConfigFile(r);
				this.globalSearchService.reload();
				this.reload.emit(true);
			},
			error => {
				console.error('Error loading configuration', error);
			}
		);

		this.subscriptions.add(loadConfigSubscription);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
